import { useMemo, useState } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import Pagination from "@/components/feature/Pagination";
import { newsList } from "@/mocks/news";

const categories = ["全部", "新番资讯", "行业动态", "作品动态", "活动资讯"];
const sortOptions = [
  { value: "newest", label: "最新发布" },
  { value: "oldest", label: "最早发布" },
];

const PAGE_SIZE = 6;

export default function NewsListPage() {
  const [category, setCategory] = useState("全部");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let list = newsList.filter((n) => category === "全部" || n.category === category);
    list = [...list].sort((a, b) =>
      sort === "newest"
        ? b.publishedAt.localeCompare(a.publishedAt)
        : a.publishedAt.localeCompare(b.publishedAt),
    );
    return list;
  }, [category, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setCategory("全部");
    setSort("newest");
    setPage(1);
  };

  const changeCategory = (value: string) => {
    setCategory(value);
    setPage(1);
  };
  const changeSort = (value: string) => {
    setSort(value);
    setPage(1);
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[{ label: "动漫资讯" }]} />

        <div className="mt-5 mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">动漫资讯</h1>
          <p className="text-sm text-foreground-500 leading-relaxed">
            汇集新番动态、行业趋势与作品情报，按时间与热度为你整理动漫圈的每一件大事。
          </p>
        </div>

        <div className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-5 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-center flex-wrap gap-2">
              <span className="text-sm font-medium text-foreground-600 mr-1">分类</span>
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => changeCategory(c)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                    category === c
                      ? "bg-primary-600 text-background-50"
                      : "bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 lg:ml-auto">
              <span className="text-sm font-medium text-foreground-600">排序</span>
              <div className="flex items-center p-1 rounded-full bg-background-50 border border-background-300">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => changeSort(opt.value)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                      sort === opt.value
                        ? "bg-primary-600 text-background-50"
                        : "text-foreground-600 hover:text-primary-700"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
              {pageItems.map((item) => (
                <NewsCard key={item.id} news={item} />
              ))}
            </div>
            <Pagination current={currentPage} total={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 flex flex-col items-center justify-center text-center">
            <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-file-search-line text-2xl"></i>
            </span>
            <p className="text-base font-medium text-foreground-800 mb-1">没有找到相关资讯</p>
            <p className="text-sm text-foreground-500 mb-5">试试切换分类或调整排序方式</p>
            <button
              type="button"
              onClick={resetFilters}
              className="px-5 h-10 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </div>
  );
}